import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useCallback,
  useRef,
} from "react";
import { useColorScheme as useMuiColorScheme } from "@mui/material/styles";

export type ColorScheme = "light" | "dark";

const STORAGE_KEY = "outpave-color-scheme";
const DEFAULT: ColorScheme = "light";
// `index.css` is the single source of truth for the crossfade duration —
// `--theme-transition-duration`. We read it at runtime so this constant can
// never drift from the CSS.
const TRANSITION_CLASS = "theme-transition";
const TRANSITION_DURATION_VAR = "--theme-transition-duration";
const FALLBACK_TRANSITION_MS = 200;

const getTransitionMs = (): number => {
  if (typeof window === "undefined") return FALLBACK_TRANSITION_MS;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(TRANSITION_DURATION_VAR)
    .trim();
  if (!raw) return FALLBACK_TRANSITION_MS;
  // Support "500ms" and "0.5s".
  if (raw.endsWith("ms")) return parseFloat(raw) || FALLBACK_TRANSITION_MS;
  if (raw.endsWith("s"))
    return (parseFloat(raw) || 0) * 1000 || FALLBACK_TRANSITION_MS;
  return parseFloat(raw) || FALLBACK_TRANSITION_MS;
};

interface ColorSchemeContextValue {
  colorScheme: ColorScheme;
  isDark: boolean;
  setColorScheme: (scheme: ColorScheme) => void;
}

const ColorSchemeContext = createContext<ColorSchemeContextValue>({
  colorScheme: DEFAULT,
  isDark: false,
  setColorScheme: () => {},
});

const resolveInitialScheme = (): ColorScheme => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
    // MUI may store "system" in the same key; fall through to the OS check.
  } catch {
    // localStorage unavailable (SSR, private browsing)
  }
  if (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return DEFAULT;
};

// Module-scope stamp, before first paint: the DS defaults to dark while
// data-theme is absent. MUI re-stamps the same value on hydration (same key).
if (typeof document !== "undefined") {
  document.documentElement.setAttribute("data-theme", resolveInitialScheme());
}

/**
 * Delegates to MUI's scheme manager (which owns data-theme and persistence);
 * adds only the crossfade class. Must mount inside DsRootTheme.
 */
export const ColorSchemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { mode, systemMode, setMode } = useMuiColorScheme();
  const resolved = mode === "system" ? systemMode : mode;
  const colorScheme: ColorScheme =
    (resolved ?? resolveInitialScheme()) === "dark" ? "dark" : "light";

  const transitionTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // Clear any pending transition timer on unmount so we don't mutate <html>
  // after the provider has been removed from the tree.
  useLayoutEffect(() => {
    return () => {
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
    };
  }, []);

  const setColorScheme = useCallback(
    (scheme: ColorScheme) => {
      const root = document.documentElement;

      // Enable color transitions only for the duration of the switch, so the
      // tokens crossfade without animating hover states or the first paint.
      root.classList.add(TRANSITION_CLASS);
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
      transitionTimer.current = setTimeout(() => {
        root.classList.remove(TRANSITION_CLASS);
      }, getTransitionMs());

      // Stamped here as well as by MUI so the scheme lands in the same frame as
      // the transition class, rather than a render later.
      root.setAttribute("data-theme", scheme);
      setMode(scheme);
    },
    [setMode],
  );

  return (
    <ColorSchemeContext.Provider
      value={{
        colorScheme,
        isDark: colorScheme === "dark",
        setColorScheme,
      }}
    >
      {children}
    </ColorSchemeContext.Provider>
  );
};

/**
 * Returns the current color scheme and helpers to change it.
 * Must be used inside `<ColorSchemeProvider>`.
 */
export function useColorScheme(): ColorSchemeContextValue {
  return useContext(ColorSchemeContext);
}
